import axios from 'axios';
import { sec } from '../utils/security';

export default class RestApiV2 {
  /**
   * @constructs SolarFarmsApi
   * Initializes axios.
   * sets default headers and baseURL for all ajax calls.
   */
  constructor(org_id = '', baseURL = '', token = null) {
    this.axiosInstance = axios.create({
      baseURL: baseURL ? baseURL : `${window.REACT_APP_API_ENDPOINT}/api/v2`,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // Sets the token generator function to the singleton function reference
    // created on app component mount
    this.tokenGenerator = sec.getAccessTokenSilently();
    //Per Auth0 recommendation, call get token on each request
    this.axiosInstance.interceptors.request.use(
      async config => {
        const token = await this.getToken();
        return {
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${token}` },
        };
      },
      error => {
        Promise.reject(error);
      },
    );

    this.org_id = org_id ? org_id : '';
    return this;
  }

  getToken() {
    return this.tokenGenerator();
  }

  generateParams = params => {
    let urlParams = '';
    let keys =
      params && Object.keys(params).length > 0 ? Object.keys(params) : [];

    if (!keys.includes('org_id') && this.org_id) {
      urlParams += `?org_id=${this.org_id}`;
      keys = keys.filter(k => k !== 'org_id');
    }

    if (keys.length) {
      keys.forEach(key => {
        if (urlParams.length === 0) {
          urlParams += '?';
        } else {
          urlParams += '&';
        }
        urlParams += `${key}=${params[key]}`;
      });
    }
    return urlParams;
  };

  static errorMessageExtractor = function (err) {
    if (err.response?.data?.match) {
      let test = err.response.data.match(/<p>(.*)<\/p>/);
      if (test && test[1]) {
        return test[1];
      }

      test = err.response.data.match(/<title>(.*)<\/title>/);
      if (test && test[1]) {
        return test[1];
      }
    }

    return err.response;
  };

  static getErrorMessageOrFallback = function (err) {
    const extractedError = {
      type: '',
      message: '',
    };
    const error = RestApiV2.errorMessageExtractor(err);

    // set error message based on returned response or status code
    if (error.data.detail) {
      extractedError.message = error.data.detail;
    } else if (error.status === 400) {
      extractedError.message =
        'Your request contains invalid data. Please check and try again.';
    } else if (error.status === 401) {
      extractedError.message =
        'Your session has expired. Please log in again to continue.';
    } else if (error.status === 403) {
      extractedError.message =
        "You don't have permission to access this resource.";
    } else if (error.status === 404) {
      extractedError.message = "We couldn't find what you're looking for.";
    } else if (error.status === 500) {
      extractedError.message =
        'Something went wrong on our end. Please try again later.';
    } else if (error.status === 503) {
      extractedError.message =
        'Service temporarily unavailable. Please try again later.';
    } else if (error.status === 504) {
      extractedError.message =
        'The server took too long to respond. Please try again later.';
    } else if (error.status === 502) {
      extractedError.message =
        'Connection to our services failed. Please try again later.';
    } else {
      extractedError.message =
        'An unknown error occurred. Please try again later.';
    }

    // set error type based on status code
    if (error.status < 500) {
      extractedError.type = 'warning';
    } else {
      extractedError.type = 'error';
    }

    return extractedError;
  };
}

RestApiV2.prototype.get = function (url, params, newErrorHandling = false) {
  const urlParams = this.generateParams(params);
  return new Promise((resolve, reject) => {
    const timeNow = new Date();
    if (process.env.LOG_LEVEL >= 2)
      console.log(`${url} get start:   ${timeNow.toTimeString()}`);
    this.axiosInstance
      .get(url + urlParams)
      .then(res => {
        if (process.env.LOG_LEVEL >= 2)
          console.log(
            `${url} get took:   ${new Date().getTime() - timeNow.getTime()} ms`,
          );
        if (!res.data.exit_status || parseInt(res.data.exit_status) === 0) {
          resolve(res.data);
        } else {
          reject(res.data);
        }
      })
      .catch(err => {
        if (newErrorHandling) {
          reject(RestApiV2.getErrorMessageOrFallback(err));
        } else {
          reject(RestApiV2.errorMessageExtractor(err));
        }
      });
  });
};

RestApiV2.prototype.post = function (
  url,
  data,
  params,
  newErrorHandling = false,
) {
  const urlParams = this.generateParams(params);

  return new Promise((resolve, reject) => {
    const timeNow = new Date();
    if (process.env.LOG_LEVEL >= 2)
      console.log(`${url} post start:   ${timeNow.toTimeString()}`);
    this.axiosInstance
      .post(url + urlParams, data)
      .then(res => {
        if (process.env.LOG_LEVEL >= 2)
          console.log(
            `${url} post took:   ${
              new Date().getTime() - timeNow.getTime()
            } ms`,
          );
        if (!res.data.exit_status || parseInt(res.data.exit_status) === 0) {
          resolve(res.data);
        } else {
          reject(res.data);
        }
      })
      .catch(err => {
        if (newErrorHandling) {
          reject(RestApiV2.getErrorMessageOrFallback(err));
        } else {
          console.log(RestApiV2.errorMessageExtractor(err));
          reject(`${url} post error: ${RestApiV2.errorMessageExtractor(err)}`);
        }
      });
  });
};

RestApiV2.prototype.put = function (
  url,
  data,
  params,
  newErrorHandling = false,
) {
  const urlParams = this.generateParams(params);

  return new Promise((resolve, reject) => {
    const timeNow = new Date();
    if (process.env.LOG_LEVEL >= 2)
      console.log(`${url} put start:   ${timeNow.toTimeString()}`);
    this.axiosInstance
      .put(url + urlParams, data)
      .then(res => {
        if (process.env.LOG_LEVEL >= 2)
          console.log(
            `${url} put took:   ${new Date().getTime() - timeNow.getTime()} ms`,
          );
        if (!res.data.exit_status || parseInt(res.data.exit_status) === 0) {
          resolve(res.data);
        } else {
          reject(res.data);
        }
      })
      .catch(err => {
        if (newErrorHandling) {
          reject(RestApiV2.getErrorMessageOrFallback(err));
        } else {
          console.log(RestApiV2.errorMessageExtractor(err));
          reject(
            RestApiV2.errorMessageExtractor(err).data.detail ??
              'An unknown error occurred. Please try again later.',
          );
        }
      });
  });
};

RestApiV2.prototype.patch = function (
  url,
  data,
  params,
  newErrorHandling = false,
) {
  const urlParams = this.generateParams(params);

  return new Promise((resolve, reject) => {
    const timeNow = new Date();
    if (process.env.LOG_LEVEL >= 2)
      console.log(`${url} patch start:   ${timeNow.toTimeString()}`);
    this.axiosInstance
      .patch(url + urlParams, data)
      .then(res => {
        if (process.env.LOG_LEVEL >= 2)
          console.log(
            `${url} patch took:   ${
              new Date().getTime() - timeNow.getTime()
            } ms`,
          );
        if (!res.data.exit_status || parseInt(res.data.exit_status) === 0) {
          resolve(res.data);
        } else {
          reject(res.data);
        }
      })
      .catch(err => {
        if (newErrorHandling) {
          reject(RestApiV2.getErrorMessageOrFallback(err));
        } else {
          console.log(RestApiV2.errorMessageExtractor(err));
          reject(`${url} patch error: ${RestApiV2.errorMessageExtractor(err)}`);
        }
      });
  });
};

RestApiV2.prototype.delete = function (
  url,
  params,
  data = {},
  newErrorHandling = false,
) {
  const urlParams = this.generateParams(params);

  return new Promise((resolve, reject) => {
    const timeNow = new Date();
    if (process.env.LOG_LEVEL >= 2)
      console.log(`${url} delete start:   ${timeNow.toTimeString()}`);
    this.axiosInstance
      .delete(url + urlParams, { data: data })
      .then(res => {
        if (process.env.LOG_LEVEL >= 2)
          console.log(
            `${url} delete took:   ${
              new Date().getTime() - timeNow.getTime()
            } ms`,
          );
        if (!res.data.exit_status || parseInt(res.data.exit_status) === 0) {
          resolve(res.data);
        } else {
          reject(res.data);
        }
      })
      .catch(err => {
        if (newErrorHandling) {
          reject(RestApiV2.getErrorMessageOrFallback(err));
        } else {
          console.log(RestApiV2.errorMessageExtractor(err));
          reject(
            `${url} delete error: ${RestApiV2.errorMessageExtractor(err)}`,
          );
        }
      });
  });
};
